import React, { useRef, useState, useMemo } from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader, Input, InputGroup, InputGroupAddon, Media } from 'reactstrap';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useSlate } from 'slate-react';

import { clientContractAction } from 'api/actions/contracts';
import { initialLetters, parseDate, format } from 'utils';
import { TOGGLE_COMMENTS, useSign, useSignState } from 'context/signview';
import { fetcher } from 'api/client';
import { getNodesById } from '../Editor/Canvas/TratoFormat/editor-utils';
import { Circle } from './SignView';

function ReplyFooter({ comment = {}, isPdf }) {

    const messageRef = useRef();

    const { t } = useTranslation();

    const queryClient = useQueryClient();

    const { commentElementId, contractId, jwt } = useSignState();

    const { mutate: doAnswerMutation } = useMutation((payload) =>
        fetcher(clientContractAction(contractId, isPdf ? 'answercomment' : 'answercommentelement', payload), jwt), {
        onSuccess: () => {
            // TODO
            // queryClient.setQueryData('client-contract', data)
            queryClient.invalidateQueries('client-contract')
        }
    })

    const handleClick = () => {
        const message = messageRef.current.value;
        if (comment) {
            doAnswerMutation({
                message,
                elementId: commentElementId,
                commentId: comment._id
            });
            messageRef.current.value = '';
        }
    }

    return (
        <InputGroup>
            <Input
                innerRef={messageRef}
                id="input-send-new-message"
                className="border-0"
                type="textarea"
                placeholder={t('Responder mensaje')}
                name="send-response">
            </Input>
            <InputGroupAddon addonType="append">
                <Button
                    size="sm"
                    color="default"
                    onClick={handleClick}>
                    <i className="fas fa-paper-plane p-1"></i>
                </Button>
            </InputGroupAddon>
        </InputGroup>
    )
}

function CommentItem({ comment = {}, isAnswer = false, isPdf }) {

    const { t } = useTranslation();
    const [hideAnswers, sethideAnswers] = useState(false)

    const toggleHideAnswer = () => {
        sethideAnswers(!hideAnswers)
    }
    return (
        <Card className={clsx('mb-2 bg-light', isAnswer && 'px-3 py-1 bg-light border-0')}>
            <CardBody className={clsx(isAnswer ? 'bg-white' : 'bg-light', 'p-3')}>
                <Media className="mb-3">
                    <Media className="mr-2">
                        <Circle text={initialLetters(comment.email)} className="bg-primary text-white" />
                    </Media>
                    <Media className="align-items-center align-self-center">
                        <small>
                            {t('sign.contract.comment_status_date', { replace: { date: format(comment.createdAt, 'Pp') } })}
                        </small>
                    </Media>
                </Media>
                <p className="ml-1">
                    {comment.message}
                </p>
                {!isAnswer &&
                    <small className="text-secondary pointer" onClick={toggleHideAnswer}>
                        <u>
                            {hideAnswers ?
                                t('sign.contract.comments_show_answers') :
                                t('sign.contract.comments_hide_answers')
                            }
                        </u>
                    </small>
                }
            </CardBody>

            {!isAnswer &&
                <>
                    {!hideAnswers && comment.answers?.map((answer) => (<CommentItem comment={answer} key={answer._id} isAnswer={true} />))}
                    <CardFooter className="p-0 m-0 bg-white">
                        <ReplyFooter comment={comment} isPdf={isPdf} />
                    </CardFooter>
                </>
            }
        </Card >

    )
}

function Comment({ comments = [], isPdf }) {

    const { t } = useTranslation();

    return comments.length ?
        comments.map((c) => (<CommentItem comment={c} key={c._id} isPdf={isPdf} />))
        :
        <p>{t(isPdf ? 'sign.contract.comments_pdf_empty' : 'sign.chat.no_comments')}</p>
}

function SendFooter({ isPdf }) {

    const { t } = useTranslation();

    const { contractId, commentElementId, jwt } = useSignState();

    const queryClient = useQueryClient();

    const { mutate: doCommentMutation } = useMutation((payload) => fetcher(clientContractAction(contractId,  isPdf ? 'comments' : 'commentelement', payload), jwt), {
        onSuccess: () => {
            // TODO
            // queryClient.setQueryData('client-contract', data)
            queryClient.invalidateQueries('client-contract')
        }
    })

    const commentRef = useRef();

    const handleSendComment = () => {
        const message = commentRef.current.value;
        doCommentMutation({ answers: [], message, elementId: commentElementId });
        commentRef.current.value = '';
    }

    return (
        <InputGroup>
            <Input
                innerRef={commentRef}
                id="input-send-new-message"
                className="border-0"
                type="textarea"
                placeholder={t('Enviar un nuevo mensaje')}
                name="new-message-content">
            </Input>
            <InputGroupAddon addonType="append">
                <Button
                    size="sm"
                    color="default"
                    onClick={handleSendComment}>
                    <i className="fas fa-paper-plane p-1"></i>
                </Button>
            </InputGroupAddon>
        </InputGroup>
    )
}

export default function CommentsBar() {

    const { t } = useTranslation();
    const editor = useSlate();

    const [{ openComments, commentElementId, contract }, dispatch] = useSign();
    const isPdf = useMemo(() => !!contract.importedPdf || !!contract.wordonline, [contract.importedPdf, contract.wordonline])

    const comments = useMemo(() => {
        if (isPdf) return contract.comments;
        if (contract.isMigrated) {
            const [element] = getNodesById(editor, commentElementId)[0] || [];
    
            return element?.signComments
        } 

        return contract.elements?.find(e => e._id === commentElementId)?.signComments;
    }, [contract.isMigrated, contract.elements, commentElementId, isPdf, contract.comments])

    if (!openComments) return null;

    const handleCloseComments = () => {
        dispatch({ type: TOGGLE_COMMENTS })
    }
  
    return (
        <Card style={{maxHeight: 'calc(100vh - 150px)'}}>
            <CardHeader className="bg-white py-3 font-weight-bold d-flex">
                {t('sign.comments.title')}
                <div className="ml-auto">
                    <i className="fas fa-times pointer" onClick={handleCloseComments}></i>
                </div>
            </CardHeader>
            <CardBody className="overflow-y-auto">
                {(commentElementId || isPdf) ?
                    <Comment comments={comments} isPdf={isPdf} />
                    :
                    <p>{t('sign.contract.element_not_selected')}</p>
                }
            </CardBody>
            {(commentElementId || isPdf) &&
                <CardFooter className="m-0 p-0 bg-white">
                    <SendFooter isPdf={isPdf} />
                </CardFooter>
            }
        </Card>
    )
}